import { SurveyPage, SurveyPageDataScale } from '@quality24/inpatient-typings';
import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  ColorStyleProps,
  Heading,
  Text,
  TextArea,
} from '@quality24/design-system';
import ImprovementsSelector from '@/inpatient-patient-pwa/molecules/ImprovementsSelector';
import MultipleChoiceInputField from '@/inpatient-patient-pwa/molecules/MultipleChoiceInputField';
import useI18nResolvers from '@/inpatient-patient-pwa/i18n/useI18nResolvers';
import useImprovements from '../useImprovements';

import { SurveyHook } from '@/inpatient-patient-pwa/hooks/useSurvey/types';

export interface Props {
  page: Pick<
    SurveyPage,
    'id' | 'category' | 'type' | 'titleI18N' | 'required' | 'descriptionI18N'
  > & {
    data?: SurveyPageDataScale<ColorStyleProps>;
  };
  answer: SurveyHook['answer'];
}

const ScaleQuestionPage: React.FunctionComponent<Props> = ({
  page,
  answer,
}) => {
  const { t } = useTranslation();
  const { resolveI18n } = useI18nResolvers();
  const improvements = useImprovements(page.data);
  const commentOptions = page.data?.commentOptions;
  return (
    <>
      <div className="mb-4">
        <Heading as="h2" weight="semiBold" size="md">
          {page.titleI18N}
        </Heading>
        {page.descriptionI18N && (
          <Text className="mt-2" style={{ whiteSpace: 'pre-wrap' }}>
            {page.descriptionI18N}
          </Text>
        )}
      </div>

      <MultipleChoiceInputField
        className="mb-5"
        options={
          page.data?.choices
            ? Object.entries(page.data?.choices).map(([key, item]) => ({
                id: String(key),
                ...item,
                label: resolveI18n(item.labelI18N) || item.label,
              }))
            : []
        }
        value={
          answer.values.value.score !== undefined
            ? String(answer.values.value.score)
            : undefined
        }
        onChange={(v) => {
          answer.setFieldValue('value.score', v);
          // Keeps the stored pt-BR labels, which the dashboards read, rather
          // than the translated ones displayed above.
          answer.setFieldValue('data', {
            mode: page.data?.mode,
            choices: page.data?.choices,
          });
        }}
      />
      {improvements.titles.length > 0 && (
        <ImprovementsSelector
          className="my-4"
          titles={improvements.titles}
          value={improvements.toDisplayed(
            answer.values.value.improvements || [],
          )}
          onChange={(v) =>
            answer.setFieldValue('value.improvements', improvements.toStored(v))
          }
        />
      )}
      {commentOptions?.enabled && (
        <TextArea
          id="formFeedback"
          className="my-4"
          label={`${
            resolveI18n(commentOptions.labelI18N) ||
            commentOptions.label ||
            t('survey.comment.label')
          }${commentOptions.required ? '*' : ''}`}
          placeholder={
            resolveI18n(commentOptions.placeholderI18N) ||
            commentOptions.placeholder
          }
          name="text"
          value={answer.values.value.text}
          errorText={answer.errors?.value?.text}
          onChange={(e) => answer.setFieldValue('value.text', e.target.value)}
          maxLength={commentOptions.maxLength || 512}
        />
      )}
    </>
  );
};

export default ScaleQuestionPage;
